// Idle-session management.
//
// The server owns the policy (config.x.session in config/application.rb) and publishes it
// through <meta> tags in the layout. This script:
//   - treats real user input as activity and shares it across tabs via localStorage,
//   - sends a throttled, CSRF-protected heartbeat so the server-side timer (Devise
//     last_request_at + the sliding session cookie) matches what the user is doing,
//   - mirrors the server's view of the deadline (last successful renewal + idle timeout),
//     warns before expiry with a "Continue working" button, and signs out when it passes.
//
// Background polling must send X-Session-Passive and is ignored by the server; nothing in
// this file fires without a DOM input event, so an unattended tab never renews a session.

const STORAGE_ACTIVITY = 'tapyr.session.lastActivity';
const STORAGE_RENEWAL = 'tapyr.session.lastRenewal';
const STORAGE_SIGNED_OUT = 'tapyr.session.signedOut';
const TICK_MS = 5000;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'wheel', 'scroll', 'touchstart', 'click'];
// Responses that mean the server no longer has a live session for this browser
const SESSION_GONE_STATUSES = [401, 403, 422];

function readMeta(name) {
  return document.querySelector(`meta[name="${name}"]`)?.getAttribute('content');
}

function readPolicy() {
  const idle = parseInt(readMeta('session-idle-seconds'), 10);
  const warning = parseInt(readMeta('session-warning-seconds'), 10);
  const heartbeat = parseInt(readMeta('session-heartbeat-seconds'), 10);
  const path = readMeta('session-heartbeat-path');
  if (!idle || !path) return null;
  return {
    idleMs: idle * 1000,
    warningMs: (warning || 0) * 1000,
    heartbeatMs: (heartbeat || 60) * 1000,
    heartbeatPath: path
  };
}

function storageGet(key) {
  try { return parseInt(window.localStorage.getItem(key), 10) || 0; } catch (_) { return 0; }
}
function storageSet(key, value) {
  try { window.localStorage.setItem(key, String(value)); } catch (_) { /* private mode etc. */ }
}

class SessionTimeoutManager {
  constructor(policy) {
    this.policy = policy;
    const now = Date.now();
    // This page load was itself an authenticated request, so the server just renewed.
    this.lastActivity = now;
    this.lastRenewal = now;
    this.pendingActivity = false;
    this.heartbeatInFlight = false;
    this.signingOut = false;
    this.warningEl = null;
    storageSet(STORAGE_ACTIVITY, now);
    storageSet(STORAGE_RENEWAL, now);

    this.onActivity = this.onActivity.bind(this);
    this.onStorage = this.onStorage.bind(this);
    this.onTurboFetch = () => this.noteRenewal();
    this.tick = this.tick.bind(this);
    this.bind();
    this.tickId = window.setInterval(this.tick, TICK_MS);
  }

  bind() {
    ACTIVITY_EVENTS.forEach((evt) => document.addEventListener(evt, this.onActivity, { passive: true }));
    document.addEventListener('submit', this.onActivity);
    // A Turbo visit is an authenticated request the user initiated: it renews server-side.
    document.addEventListener('turbo:before-fetch-request', this.onTurboFetch);
    window.addEventListener('storage', this.onStorage);
  }

  unbind() {
    ACTIVITY_EVENTS.forEach((evt) => document.removeEventListener(evt, this.onActivity));
    document.removeEventListener('submit', this.onActivity);
    document.removeEventListener('turbo:before-fetch-request', this.onTurboFetch);
    window.removeEventListener('storage', this.onStorage);
  }

  // ---- activity ---------------------------------------------------------------------

  onActivity() {
    const now = Date.now();
    if (now - this.lastActivity > 1000) storageSet(STORAGE_ACTIVITY, now);
    this.lastActivity = now;
    this.pendingActivity = true;
    if (this.warningEl) this.maybeHeartbeat(true);
  }

  onStorage(event) {
    if (event.key === STORAGE_ACTIVITY) {
      const ts = parseInt(event.newValue, 10) || 0;
      if (ts > this.lastActivity) { this.lastActivity = ts; this.pendingActivity = true; }
    } else if (event.key === STORAGE_RENEWAL) {
      const ts = parseInt(event.newValue, 10) || 0;
      if (ts > this.lastRenewal) { this.lastRenewal = ts; this.pendingActivity = false; this.hideWarning(); }
    } else if (event.key === STORAGE_SIGNED_OUT && event.newValue) {
      // Another tab was signed out; follow it, for the same reason, rather than show stale data.
      this.goToSignIn(signedOutReason(event.newValue));
    }
  }

  noteRenewal() {
    const now = Date.now();
    this.lastRenewal = now;
    this.pendingActivity = false;
    storageSet(STORAGE_RENEWAL, now);
    this.hideWarning();
  }

  // ---- server coordination ------------------------------------------------------------

  deadline() { return this.lastRenewal + this.policy.idleMs; }

  maybeHeartbeat(force = false) {
    if (this.heartbeatInFlight || this.signingOut) return;
    const now = Date.now();
    if (!this.pendingActivity) return;
    // Only one tab needs to renew per interval; another tab may have just done it.
    const sharedRenewal = storageGet(STORAGE_RENEWAL);
    if (sharedRenewal > this.lastRenewal) { this.lastRenewal = sharedRenewal; this.pendingActivity = false; return; }
    if (!force && now - this.lastRenewal < this.policy.heartbeatMs) return;
    this.sendHeartbeat();
  }

  sendHeartbeat() {
    const token = readMeta('csrf-token');
    this.heartbeatInFlight = true;
    fetch(this.policy.heartbeatPath, {
      method: 'POST',
      credentials: 'same-origin',
      redirect: 'manual',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': token || ''
      }
    }).then((response) => {
      this.heartbeatInFlight = false;
      if (SESSION_GONE_STATUSES.includes(response.status)) {
        // The server already expired or rejected the session; a late heartbeat must not revive it.
        // 422 is the CSRF check failing because the session cookie itself is gone.
        this.redirectToSignIn('idle');
      } else if (response.ok) {
        this.noteRenewal();
      }
      // Anything else (redirect to MFA, 5xx, network hiccup) is retried on the next tick.
    }).catch(() => { this.heartbeatInFlight = false; });
  }

  // ---- timers ----------------------------------------------------------------------

  tick() {
    if (this.signingOut) return;
    const now = Date.now();
    this.maybeHeartbeat();
    let remaining = this.deadline() - now;
    if (remaining <= 0) {
      // A throttled background tab can miss `storage` events; re-read the shared renewal before
      // deciding the session is idle, or this tab signs out a user active in another tab.
      const sharedRenewal = storageGet(STORAGE_RENEWAL);
      if (sharedRenewal > this.lastRenewal) {
        this.lastRenewal = sharedRenewal;
        remaining = this.deadline() - now;
      }
    }
    if (remaining <= 0) {
      this.signOut();
    } else if (this.policy.warningMs > 0 && remaining <= this.policy.warningMs) {
      this.showWarning(remaining);
    } else {
      this.hideWarning();
    }
  }

  // ---- warning UI ------------------------------------------------------------------

  showWarning(remainingMs) {
    const minutes = Math.max(1, Math.ceil(remainingMs / 60000));
    const text = `You will be signed out in about ${minutes} minute${minutes === 1 ? '' : 's'} due to inactivity.`;
    if (this.warningEl) {
      this.warningEl.querySelector('[data-session-countdown]').textContent = text;
      return;
    }
    const overlay = document.createElement('div');
    overlay.setAttribute('role', 'alertdialog');
    overlay.setAttribute('aria-live', 'assertive');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:2000;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.45);';
    overlay.innerHTML = `
      <div style="background:var(--bs-body-bg,#fff);color:var(--bs-body-color,#212529);border-radius:.5rem;padding:1.5rem;max-width:26rem;box-shadow:0 .5rem 1.5rem rgba(0,0,0,.3);">
        <h5 style="margin-top:0">Still working?</h5>
        <p data-session-countdown>${text}</p>
        <p class="text-muted small">Any unsaved changes will be lost if the session expires.</p>
        <div style="display:flex;gap:.5rem;justify-content:flex-end">
          <button type="button" class="btn btn-outline-secondary" data-session-signout>Sign out now</button>
          <button type="button" class="btn btn-primary" data-session-continue autofocus>Continue working</button>
        </div>
      </div>`;
    overlay.querySelector('[data-session-continue]').addEventListener('click', () => {
      this.pendingActivity = true;
      this.maybeHeartbeat(true);
    });
    overlay.querySelector('[data-session-signout]').addEventListener('click', () => this.signOut('manual'));
    document.body.appendChild(overlay);
    this.warningEl = overlay;
    overlay.querySelector('[data-session-continue]').focus();
  }

  hideWarning() {
    if (!this.warningEl) return;
    this.warningEl.remove();
    this.warningEl = null;
  }

  // ---- sign out --------------------------------------------------------------------

  // Ends the session. The server may already have expired it (its timer and ours run in step), in
  // which case a sign-out POST would fail its CSRF check and show an error page; so probe first and
  // only POST the sign-out when the session is still alive.
  signOut(reason = 'idle') {
    if (this.signingOut) return;
    this.signingOut = true;
    window.clearInterval(this.tickId);
    storageSet(STORAGE_SIGNED_OUT, JSON.stringify({ at: Date.now(), reason }));

    fetch(this.policy.heartbeatPath, {
      method: 'POST',
      credentials: 'same-origin',
      redirect: 'manual',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-CSRF-Token': readMeta('csrf-token') || '' }
    }).then((response) => {
      // Only a server that already rejected the session lets us skip the sign-out POST. On an MFA
      // redirect, a 5xx or anything else the session may still be alive and must be ended, or the
      // sign-in page would just bounce the next person at this terminal back into the app.
      if (SESSION_GONE_STATUSES.includes(response.status)) this.goToSignIn(reason); else this.postSignOut(reason);
    }).catch(() => this.postSignOut(reason));
  }

  postSignOut(reason) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `/users/sign_out?reason=${encodeURIComponent(reason)}`;
    form.style.display = 'none';
    const add = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = name; input.value = value;
      form.appendChild(input);
    };
    const token = readMeta('csrf-token');
    if (token) add('authenticity_token', token);
    add('_method', 'delete');
    document.body.appendChild(form);
    form.submit();
  }

  redirectToSignIn(reason) {
    if (this.signingOut) return;
    this.signingOut = true;
    window.clearInterval(this.tickId);
    storageSet(STORAGE_SIGNED_OUT, JSON.stringify({ at: Date.now(), reason }));
    this.goToSignIn(reason);
  }

  goToSignIn(reason) {
    window.location.assign(`/users/sign_in?reason=${encodeURIComponent(reason)}`);
  }

  destroy() {
    window.clearInterval(this.tickId);
    this.unbind();
    this.hideWarning();
  }
}

// The cross-tab sign-out marker carries its reason; an older plain-timestamp value means idle.
function signedOutReason(value) {
  try {
    const reason = JSON.parse(value)?.reason;
    return typeof reason === 'string' && reason ? reason : 'idle';
  } catch (_) {
    return 'idle';
  }
}

function isAuthPage() {
  const path = window.location.pathname;
  return ['/users/sign_in', '/users/sign_up', '/users/password', '/users/invitation',
    '/users/confirmation', '/new_user_mfa_session', '/setup_mfa'].some((p) => path.startsWith(p));
}

function boot() {
  const policy = readPolicy();
  if (!policy || isAuthPage()) {
    if (window.sessionTimeoutManager) { window.sessionTimeoutManager.destroy(); window.sessionTimeoutManager = null; }
    return;
  }
  if (window.sessionTimeoutManager) {
    // New page rendered by an authenticated request: the server renewed.
    window.sessionTimeoutManager.noteRenewal();
  } else {
    window.sessionTimeoutManager = new SessionTimeoutManager(policy);
  }
}

document.addEventListener('DOMContentLoaded', boot);
document.addEventListener('turbo:load', boot);
